import React from 'react';
import { Phone, AlertTriangle, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import ModernDocHeader from '../../../components/documentation/ModernDocHeader';

const PhoneIntegrationDocs: React.FC = () => {
  const webhookUrl = `${window.location.origin}/api/webhooks`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <ModernDocHeader
        title="Phone Integration"
        description="Connect your phone system to automatically capture leads from incoming and outgoing calls. We support Knowlarity, Mcube, Ozonetel, and TCPN."
        icon={<Phone className="w-6 h-6" />}
        badge="Popular"
        badgeColor="green"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* Supported Providers */}
      <h2>Supported Providers</h2>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Knowlarity</h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">Cloud telephony platform with call tracking and analytics</p>
        </div>
        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">Mcube</h4>
          <p className="text-sm text-green-700 dark:text-green-300">Business calling solution with IVR and call routing</p>
        </div>
        <div className="p-4 border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-2">Ozonetel</h4>
          <p className="text-sm text-purple-700 dark:text-purple-300">Contact center platform with advanced call features</p>
        </div>
        <div className="p-4 border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <h4 className="font-semibold text-orange-900 dark:text-orange-200 mb-2">TCPN</h4>
          <p className="text-sm text-orange-700 dark:text-orange-300">Telecom service provider with call management</p>
        </div>
      </div>

      {/* What Gets Captured */}
      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              What Gets Captured Automatically
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Customer Phone Number</strong> - Caller's phone number</li>
              <li>✓ <strong>Call Duration</strong> - Total call time in seconds</li>
              <li>✓ <strong>Call Type</strong> - Incoming or outgoing</li>
              <li>✓ <strong>Call Recording URL</strong> - Audio recording of the call</li>
              <li>✓ <strong>Call Start & End Time</strong> - Timestamp of call</li>
              <li>✓ <strong>Agent Information</strong> - Who handled the call</li>
              <li>✓ <strong>Call Status/Result</strong> - Connected, missed, voicemail, etc.</li>
              <li>✓ <strong>Unique Call ID</strong> - For tracking and reference</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Setup Guide</h2>

      <h3>Step 1: Get Your SR Number</h3>
      <p>
        An SR Number (Service Request Number) is a unique 10-digit identifier that links your phone provider
        to your CRM account.
      </p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Where to find your SR Number:</p>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li><strong>Knowlarity:</strong> Dashboard → Settings → SR Number</li>
            <li><strong>Mcube:</strong> Account Settings → API Configuration</li>
            <li><strong>Ozonetel:</strong> CloudAgent → Settings → API Details</li>
            <li><strong>TCPN:</strong> Dashboard → Integrations → Webhook Settings</li>
          </ul>
        </div>
      </div>

      <h3>Step 2: Add SR Number to CRM</h3>
      <ol>
        <li>Navigate to <code>Settings → Phone Integration</code></li>
        <li>Enter your 10-digit SR Number</li>
        <li>Click "Add Integration"</li>
        <li>Your phone number will be registered and activated immediately</li>
      </ol>

      <h3>Step 3: Configure Webhook in Your Phone Provider</h3>
      <p>
        Now you need to tell your phone provider to send call data to your CRM. Here are the webhook URLs:
      </p>

      <div className="not-prose space-y-3 my-6">
        {/* Knowlarity */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">Knowlarity Webhook URL</p>
            <button
              onClick={() => copyToClipboard(`${webhookUrl}/knowlarity`)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}/knowlarity
          </code>
        </div>

        {/* Mcube */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">Mcube Webhook URL</p>
            <button
              onClick={() => copyToClipboard(`${webhookUrl}/mcube`)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}/mcube
          </code>
        </div>

        {/* Ozonetel */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">Ozonetel Webhook URL</p>
            <button
              onClick={() => copyToClipboard(`${webhookUrl}/ozonetel`)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}/ozonetel
          </code>
        </div>

        {/* TCPN */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">TCPN Webhook URL</p>
            <button
              onClick={() => copyToClipboard(`${webhookUrl}/tcpn`)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}/tcpn
          </code>
        </div>
      </div>

      <h3>Step 4: Configure Webhook Settings</h3>
      <p>In your phone provider's dashboard:</p>
      <ol>
        <li>Navigate to Webhook / API Settings</li>
        <li>Add a new webhook endpoint</li>
        <li>Paste the appropriate webhook URL from above</li>
        <li>Set the method to <code>POST</code></li>
        <li>Enable the following events:
          <ul>
            <li>Call Connected</li>
            <li>Call Completed</li>
            <li>Call Missed</li>
          </ul>
        </li>
        <li>Save the webhook configuration</li>
      </ol>

      <h3>Step 5: Test the Integration</h3>
      <ol>
        <li>Make a test call to your phone number</li>
        <li>Check your CRM Dashboard for the new lead</li>
        <li>Verify that call details are captured:
          <ul>
            <li>Phone number</li>
            <li>Call duration</li>
            <li>Recording URL (if available)</li>
          </ul>
        </li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-6">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Test with your own number first to verify the integration works before going live.
        </p>
      </div>

      <h2>How It Works</h2>

      <h3>Call Flow</h3>
      <ol>
        <li><strong>Customer Calls</strong> - Incoming call to your phone number</li>
        <li><strong>Provider Processes</strong> - Your phone provider handles the call</li>
        <li><strong>Webhook Triggered</strong> - Provider sends call data to CRM</li>
        <li><strong>CRM Processes</strong>:
          <ul>
            <li>Validates SR Number</li>
            <li>Checks for duplicate lead (based on phone number)</li>
            <li>Creates new lead or updates existing one</li>
            <li>Saves call log with recording</li>
            <li>Assigns lead to team member (if Round Robin enabled)</li>
            <li>Sends email notification (if enabled)</li>
          </ul>
        </li>
        <li><strong>Lead Available</strong> - Lead appears in dashboard instantly</li>
      </ol>

      <h3>Duplicate Lead Handling</h3>
      <p>
        If a customer calls multiple times, the CRM intelligently handles duplicates:
      </p>
      <ul>
        <li><strong>Primary Lead:</strong> First call creates the main lead record</li>
        <li><strong>Follow-up Calls:</strong> Additional calls are logged as activities</li>
        <li><strong>Call History:</strong> All calls are tracked with recordings</li>
        <li><strong>Remarks:</strong> Each call adds a new remark with details</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Example: Multiple Calls</p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
            <span className="font-mono bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">Call 1</span>
            <span>→ Creates new lead with phone 9876543210</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
            <span className="font-mono bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">Call 2</span>
            <span>→ Adds call log to existing lead 9876543210</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
            <span className="font-mono bg-blue-100 dark:bg-blue-900 px-2 py-1 rounded">Call 3</span>
            <span>→ Adds another call log to same lead</span>
          </div>
        </div>
      </div>

      <h2>Call Recordings</h2>
      <p>
        Call recordings are automatically saved and accessible from the lead detail page:
      </p>
      <ul>
        <li>Navigate to Leads → Click on a lead</li>
        <li>Go to the <strong>Activity</strong> or <strong>Remarks</strong> tab</li>
        <li>Find remarks with audio icon</li>
        <li>Click to play the recording directly in browser</li>
      </ul>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              Important Notes
            </h4>
            <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-300">
              <li>• Recording URLs are provided by your phone provider</li>
              <li>• Recording availability depends on your phone provider's plan</li>
              <li>• Recordings may expire after 30-90 days (provider dependent)</li>
              <li>• Ensure your phone provider has recording enabled in their settings</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Troubleshooting</h2>

      <h3>Leads Not Appearing?</h3>
      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            ✓ Check SR Number is correct
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Go to Settings → Phone Integration and verify your 10-digit SR Number matches your phone provider's dashboard.
          </p>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            ✓ Verify Webhook URL is configured
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Log into your phone provider and check that the webhook URL is correctly set and active.
          </p>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            ✓ Check webhook method is POST
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Webhooks must use the POST method. GET requests will not work.
          </p>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            ✓ Test with provider's webhook testing tool
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Most providers have a "Test Webhook" button. Use this to send a sample payload and verify connectivity.
          </p>
        </details>
      </div>

      <h3>No Call Recording?</h3>
      <ul>
        <li>Enable call recording in your phone provider's settings</li>
        <li>Check if your plan includes call recording feature</li>
        <li>Some providers have separate recording endpoints - consult their documentation</li>
      </ul>

      <h3>Duplicate Leads Being Created?</h3>
      <p>
        The system uses phone number for duplicate detection. If duplicates are created:
      </p>
      <ul>
        <li>Check that phone numbers are in consistent format (last 10 digits used for matching)</li>
        <li>Verify webhook is not triggering multiple times for same call</li>
        <li>Check provider's webhook logs for duplicate sends</li>
      </ul>

      <h2>Advanced Configuration</h2>

      <h3>Multiple Phone Numbers</h3>
      <p>
        You can add multiple SR Numbers for different:
      </p>
      <ul>
        <li>Business locations (Branch A, Branch B)</li>
        <li>Departments (Sales, Support)</li>
        <li>Campaigns (TV Ad, Google Ad)</li>
      </ul>
      <p>
        Simply add each SR Number in Settings → Phone Integration. All calls will flow into the same CRM.
      </p>

      <h3>Custom Lead Assignment by Phone Number</h3>
      <p>
        Use Round Robin groups to automatically assign leads from specific phone numbers:
      </p>
      <ol>
        <li>Go to Settings → Round Robin</li>
        <li>Create a group (e.g., "Branch A Sales Team")</li>
        <li>Add team members to the group</li>
        <li>Set trigger condition: <code>Source = Phone</code> and <code>SR Number = XXXXXXXXXX</code></li>
      </ol>

      <h2>API Reference</h2>
      <p>For developers integrating with our system:</p>

      <h3>Webhook Payload Structure</h3>
      <p>The CRM expects the following data fields (provider-specific):</p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg my-4 overflow-x-auto">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Example: TCPN Webhook Payload</p>
          <pre className="text-sm"><code>{`{
  "PhoneNumber": "9876543210",        // SR Number (Client identifier)
  "CallerId": "9123456789",          // Customer phone number
  "CallType": "inbound",             // inbound | outbound
  "Duration": 120,                    // Call duration in seconds
  "CallSid": "unique-call-id-123",   // Unique call identifier
  "RecordingUrl": "https://...",     // Call recording URL
  "TimeOfCall": "2024-01-15 10:30",  // Call start time
  "EndTimeOfCall": "2024-01-15 10:32", // Call end time
  "Result": "connected",              // Call result/status
  "agentInfo": {
    "agentName": "John Doe",
    "agentUserName": "john.doe"
  }
}`}</code></pre>
        </div>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              You're All Set!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              Your phone integration is now configured. Calls will automatically create leads in your CRM.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="/settings/phone-integration"
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" />
                Go to Phone Settings
              </a>
              <a
                href="/docs/lead-management"
                className="inline-flex items-center px-3 py-2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 border border-green-600 dark:border-green-400 text-sm rounded-lg hover:bg-green-50 dark:hover:bg-gray-700 transition-colors"
              >
                Learn Lead Management
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneIntegrationDocs;
